import firebase from 'firebase/compat/app';
import 'firebase/compat/functions';
import firebaseApp, { db } from 'services/firebase';
import { secureDigits } from 'utils/secureRandom';

const TOKEN_COLLECTION = 'msi3CmrAccessTokens';
const TOKEN_VALIDITY_MINUTES = 60;
const REDEEM_FUNCTION = 'redeemHealthRecordShareToken';
const PUSH_DISCHARGE_FUNCTION = 'pushDischargeInstructions';

export const SHAREABLE_SECTIONS = [
  { id: 'demographics', label: 'Demographics' },
  { id: 'conditions', label: 'Medical Conditions' },
  { id: 'surgical', label: 'Surgical History' },
  { id: 'medications', label: 'Medications' },
  { id: 'allergies', label: 'Allergies' },
  { id: 'other', label: 'Other History' },
];

const generateManualCode = () => secureDigits(7);

export const formatManualCode = (code) => {
  if (!code || code.length !== 7) return code || '';
  return `${code.slice(0, 3)} ${code.slice(3)}`;
};

export const normalizeManualCode = (input) => (input || '').replace(/\D/g, '').slice(0, 7);

export const getTokenValidityMs = () => TOKEN_VALIDITY_MINUTES * 60 * 1000;

export const getTokenValidityLabel = () => `${TOKEN_VALIDITY_MINUTES} minutes`;

/** Discriminator for TCI-session consent tokens (vs clinician QR share). */
export const TCI_SESSION_TOKEN_CONTEXT = 'tci-session';

/**
 * Create a share token. HIPAA: the token doc itself contains NO PHI — only the
 * patient's uid, the consented section IDs, the manual code, and timestamps.
 * Patient name and DOB are resolved server-side at redemption time, so a
 * misconfigured rule on this collection can't leak identity.
 *
 * Optional TCI-session flavor (`context: "tci-session"`): binds token to
 * `tciSessionId` + `tciId` for session-scoped consent redemption.
 */
export const createShareToken = async ({
  user,
  allowedSections,
  context,
  tciSessionId,
  tciId,
}) => {
  if (!user?.uid) throw new Error('Not signed in.');
  const enabled = Object.entries(allowedSections || {})
    .filter(([, on]) => !!on)
    .map(([id]) => id);

  if (!enabled.length) {
    throw new Error('Select at least one health-record topic to share.');
  }

  const manualCode = generateManualCode();
  const expiresAtMs = Date.now() + getTokenValidityMs();

  const payload = {
    patientUserId: user.uid,
    manualCode,
    allowedSections: enabled,
    expiresAt: new Date(expiresAtMs),
    used: false,
    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
  };

  if (context === TCI_SESSION_TOKEN_CONTEXT) {
    const sessionId =
      typeof tciSessionId === 'string' ? tciSessionId.trim() : '';
    const tid = typeof tciId === 'string' ? tciId.trim() : '';
    if (!sessionId || !tid) {
      throw new Error(
        'tciSessionId and tciId are required for tci-session consent tokens.',
      );
    }
    payload.context = TCI_SESSION_TOKEN_CONTEXT;
    payload.tciSessionId = sessionId;
    payload.tciId = tid;
  }

  const docRef = await db.collection(TOKEN_COLLECTION).add(payload);

  return { tokenId: docRef.id, manualCode, expiresAtMs };
};

/**
 * Revoke a token the patient previously generated (sets used=true so it can no
 * longer be redeemed). Safe to call even if the token has already been used.
 */
export const revokeShareToken = async (tokenId) => {
  if (!tokenId) return;
  await db.collection(TOKEN_COLLECTION).doc(tokenId).update({ used: true });
};

/**
 * QR payload. We encode a deep link to the public /share landing page so a
 * provider can scan with any phone camera and be guided to view the record (or
 * create an account). The URL carries only the 7-digit manual code, which by
 * itself is NOT PHI (no patient identifiers) — identity is resolved server-side
 * at redemption. Our own in-app scanner reads the same URL via parseScannedShareCode.
 */
export const buildQrPayload = ({ manualCode }) => {
  const origin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : '';
  return `${origin}/share?c=${encodeURIComponent(manualCode)}`;
};

/**
 * Pull a 7-digit share code out of a scanned QR string. Handles the current
 * /share?c=NNNNNNN URL, the legacy JSON payload (codes minted before the URL
 * switch are still valid within their 60-minute window), and a bare 7-digit
 * string. Returns the normalized code, or null when none is present.
 */
export const parseScannedShareCode = (raw) => {
  if (!raw) return null;

  // Current form: an absolute /share?c=1234567 URL.
  try {
    const fromUrl = normalizeManualCode(new URL(raw).searchParams.get('c'));
    if (fromUrl.length === 7) return fromUrl;
  } catch {
    // not a URL — fall through
  }

  // Legacy form: the JSON payload we used to encode.
  try {
    const fromJson = normalizeManualCode(JSON.parse(raw)?.manualCode);
    if (fromJson.length === 7) return fromJson;
  } catch {
    // not JSON — fall through
  }

  const digits = normalizeManualCode(raw);
  return digits.length === 7 ? digits : null;
};

/**
 * Redeem a 7-digit manual code via Cloud Function. The function uses the admin
 * SDK to look up the token, validate it, mark it used, fetch the patient's
 * consented record sections, and write an audit log entry. The client never
 * reads the token doc or the patient's user doc directly.
 */
export const consumeShareCodeAndFetchRecord = async ({
  manualCode,
  accessContext,
  acceptCareStart = false,
}) => {
  const cleanCode = normalizeManualCode(manualCode);
  if (cleanCode.length !== 7) {
    throw new Error('Enter the full 7-digit code.');
  }

  const callable = firebaseApp.functions().httpsCallable(REDEEM_FUNCTION);
  try {
    const payload = { manualCode: cleanCode };
    if (accessContext) payload.accessContext = accessContext;
    if (acceptCareStart) payload.acceptCareStart = true;
    const { data } = await callable(payload);
    return data;
  } catch (err) {
    // Surface the function's message verbatim so the user sees the actual cause
    // (expired, used, unknown code) instead of a generic error.
    throw new Error(err?.message || 'Could not validate that code.');
  }
};

/**
 * Reverse direction of consumeShareCodeAndFetchRecord: send finalized discharge
 * instructions back to a patient whose record was imported into this encounter.
 * No code/QR needed — the provider already holds the patient's uid. The Cloud
 * Function re-verifies consent (a prior redemption audit row) before writing to
 * the patient's server-write-only dischargeInstructions subcollection.
 */
export const sendDischargeInstructions = async ({ patientUserId, discharge }) => {
  if (!patientUserId) throw new Error('No imported patient to send to.');
  const callable = firebaseApp.functions().httpsCallable(PUSH_DISCHARGE_FUNCTION);
  try {
    const { data } = await callable({ patientUserId, discharge });
    return data;
  } catch (err) {
    throw new Error(err?.message || 'Could not send discharge instructions.');
  }
};
